import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {CodeownersPathOwnership, Codeowners} from '../payloads/file-tree'
import {useQuery, type QueryKey} from '@github-ui/react-query'
import {useCallback} from 'react'

export function pullRequestCodeownersKey(basePath: string): QueryKey {
  return [PageData.codeowners, basePath]
}

function pullRequestCodeownersPayload(basePath: string) {
  const queryKey = pullRequestCodeownersKey(basePath)
  return {
    queryKey,
    queryFn: async () => {
      // we're not fetching from an API yet, seeding TSQ with payload data for re-use
      return undefined
    },
  }
}

const defaultOwnership: CodeownersPathOwnership = {
  isOwnedByViewer: false,
  owners: [],
  ruleLineNumber: undefined,
  ruleUrl: undefined,
}

/*
 * Returns ownership data from the CODEOWNERS file
 *   - If a diff path is given, returns ownership information for only that file. If codeowners data is not available,
 *     returns default ownership (unowned)
 *   - If no diff path is given, returns ownership information for all files in the pull request
 */
export function useCodeownersData({
  basePath,
  diffPath,
  initialData,
}: {
  basePath: string
  diffPath?: string
  initialData?: Codeowners
}) {
  const {queryFn, queryKey} = pullRequestCodeownersPayload(basePath)
  return useQuery({
    queryKey,
    queryFn,
    initialData,
    staleTime: Infinity,
    select: useCallback((data?: Codeowners) => getOwnershipByPath({codeownersData: data, diffPath}), [diffPath]),
  })
}

export function getOwnershipByPath({diffPath, codeownersData}: {diffPath?: string; codeownersData?: Codeowners}) {
  if (!diffPath) return codeownersData
  if (!codeownersData) return defaultOwnership

  const pathInfo = codeownersData.ownershipByPath[diffPath]
  if (!pathInfo) return defaultOwnership

  return {
    isOwnedByViewer: pathInfo.isOwnedByViewer,
    owners: pathInfo.owners || [],
    ruleLineNumber: pathInfo.ruleLineNumber,
    ruleUrl: pathInfo.ruleUrl,
  }
}
